/**
 * ReopenWithTemplateModal
 *
 * Shown when a WhatsApp 24-hour window is closed.
 * Reuses TemplateList + WhatsAppMessagePreviewEditable (same as bulk campaign screen).
 * Sends via createWhatsAppMessageTask with a single contact so the backend uses
 * the same component-building logic as bulk — avoiding parameter-count mismatches.
 *
 * Layout:
 *  - Mobile: slides up from bottom
 *  - Web: bottom sheet anchored inside the conversation column (not full-screen)
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
  useWindowDimensions,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import { useCustomTemplates } from './WhatsAppMessageForm/hooks/useCustomTemplates';
import { TemplateList } from './WhatsAppMessageForm/components/TemplateList';
import { WhatsAppMessagePreviewEditable } from './WhatsAppMessageForm/components/WhatsAppMessagePreviewEditable';
import { CustomTemplate, HeaderAsset } from './WhatsAppMessageForm/types/messageForm.types';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface Props {
  visible: boolean;
  initialTemplateId?: string;
  contactPhone: string;
  contactName?: string;
  onClose: () => void;
  onSent: () => void;
}

type Step = 'list' | 'compose';

/** Same logic as SendWhatsAppMessageScreen.derivedTemplateParameters */
function deriveParams(template: CustomTemplate) {
  if (template.parameters && template.parameters.length > 0) {
    return template.parameters;
  }
  const positionsBySection = new Map<number, Set<'header' | 'body' | 'footer'>>();
  const extract = (text: string | undefined, section: 'header' | 'body' | 'footer') => {
    if (!text) return;
    const regex = /\{\{(\d+)\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
      const pos = Number(m[1]);
      if (!positionsBySection.has(pos)) positionsBySection.set(pos, new Set());
      positionsBySection.get(pos)!.add(section);
    }
  };
  extract(template.templateHeader, 'header');
  extract(template.templateBody, 'body');
  extract(template.templateFooter, 'footer');
  return Array.from(positionsBySection.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([position, sections]) => ({
      position,
      section: sections.has('body') ? 'body' : sections.has('footer') ? 'footer' : 'header',
      name: `Parameter ${position}`,
      isRequired: true,
    }));
}

export const ReopenWithTemplateModal: React.FC<Props> = ({
  visible, contactPhone, contactName, onClose, onSent, initialTemplateId,
}) => {
  const s = useThemedStyles(getS);
  const { width: winWidth } = useWindowDimensions();
  const isWeb = Platform.OS === 'web' && winWidth >= 768;

  const { currentBusiness, currentSubscription, user } = useAppStore();
  const { templates, isLoading: isLoadingTemplates, hasTemplates } = useCustomTemplates();

  const [step, setStep] = useState<Step>('list');
  const [selected, setSelected] = useState<CustomTemplate | null>(null);
  const [templateParameters, setTemplateParameters] = useState<Record<number, string>>({});
  const [headerAsset, setHeaderAsset] = useState<HeaderAsset | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const selectionVersionRef = useRef(0);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const reset = () => {
    ++selectionVersionRef.current;
    setStep('list');
    setSelected(null);
    setTemplateParameters({});
    setHeaderAsset(undefined);
    setSending(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSelectTemplate = async (template: CustomTemplate) => {
    const version = ++selectionVersionRef.current;
    let tpl = template;
    if (template.hasParameters && (!template.parameters || template.parameters.length === 0)) {
      try {
        const detail = await apiService.getWhatsAppCustomTemplate(template._id);
        if (detail?.data?._id) tpl = detail.data;
      } catch {}
    }
    if (!visibleRef.current || selectionVersionRef.current !== version) return;
    setSelected(tpl);
    setTemplateParameters({});
    setHeaderAsset(tpl.headerAsset || undefined);
    setStep('compose');
  };

  const initialSelectionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) { initialSelectionRef.current = null; return; }
    if (!initialTemplateId || initialSelectionRef.current === initialTemplateId) return;
    const template = templates.find(t => t._id === initialTemplateId);
    if (!template) return;
    initialSelectionRef.current = initialTemplateId;
    void handleSelectTemplate(template);
  }, [visible, initialTemplateId, templates]);

  // All params derived same way as bulk screen
  const allParams = useMemo(() => selected ? deriveParams(selected) : [], [selected]);

  // Missing required params (non-header for the missing-highlight, but check all for canSend)
  const missingPositions = useMemo(
    () => allParams.filter(p => p.isRequired && !templateParameters[p.position]?.trim()).map(p => p.position),
    [allParams, templateParameters],
  );

  const canSend = useMemo(() => {
    if (!selected) return false;
    if (missingPositions.length > 0) return false;
    const assetType = selected.headerAsset?.type;
    if (assetType) {
      if (assetType === 'location') return !!(headerAsset?.mediaId);
      return !!(headerAsset?.url);
    }
    return true;
  }, [selected, missingPositions, headerAsset]);

  const handleSend = async () => {
    if (!selected || !canSend) return;

    const wabuService = currentSubscription?.services?.wabu as any;
    if (!wabuService?.metadata?.whatsapp_phone?.length) {
      Alert.alert('Configuration Error', 'No WhatsApp phone number configured.');
      return;
    }

    // Build flat parameters array sorted by position (same as bulk screen Step 2→3)
    const sortedParams = allParams
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(p => templateParameters[p.position] ?? '');

    setSending(true);
    try {
      // Use createWhatsAppMessageTask with a single contact — backend builds components
      // correctly using the saved template definition, avoiding param-count mismatches.
      const response = await apiService.createWhatsAppMessageTask({
        contacts: [{
          name: contactName || contactPhone,
          phoneNumber: contactPhone,
          countryCode: '',
          dialCode: '',
        }],
        message: selected.templateBody,
        taskName: `Reopen conversation - ${contactName || contactPhone}`,
        templateName: selected.templateName,
        templateLanguage: selected.language,
        parameters: sortedParams,
        headerAsset: headerAsset?.mediaId || headerAsset?.url ? headerAsset as any : undefined,
      });

      if (response.statusCode === 1000) {
        reset();
        onSent();
      } else {
        Alert.alert('Failed to send', response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (e: any) {
      Alert.alert('Error', e?.message || 'Failed to send template message.');
    } finally {
      setSending(false);
    }
  };

  const sheetContent = (
    <>
      {/* Header bar */}
      <View style={s.headerBar}>
        {step === 'compose' ? (
          <TouchableOpacity onPress={() => setStep('list')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <DirectionalIcon name="chevron-back" size={22} color={colors.primary} />
          </TouchableOpacity>
        ) : (
          <View style={{ width: 22 }} />
        )}
        <Text style={s.headerTitle}>
          {step === 'list' ? 'Select Template' : 'Fill & Send'}
        </Text>
        <TouchableOpacity onPress={handleClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="close" size={22} color={colors.text.primary} />
        </TouchableOpacity>
      </View>

      {step === 'list' ? (
        <>
          <Text style={s.sub}>
            The 24-hour window is closed. Pick an approved template to reopen the conversation.
          </Text>
          <TemplateList
            templates={templates}
            isLoading={isLoadingTemplates}
            hasTemplates={hasTemplates}
            onSelectTemplate={handleSelectTemplate}
            onClose={handleClose}
          />
        </>
      ) : (
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingBottom: 16 }}>

            <View style={s.selectedBadge}>
              <Ionicons name="document-text-outline" size={14} color={colors.primary} />
              <Text style={s.selectedBadgeText}>{selected?.templateName}</Text>
              <TouchableOpacity onPress={() => setStep('list')} style={{ marginLeft: 'auto' as any }}>
                <Text style={s.changeText}>Change</Text>
              </TouchableOpacity>
            </View>

            {selected && (
              <WhatsAppMessagePreviewEditable
                template={selected}
                templateParameters={templateParameters}
                onParameterChange={(pos, val) =>
                  setTemplateParameters(prev => ({ ...prev, [pos]: val }))
                }
                headerAsset={headerAsset}
                onAssetChange={setHeaderAsset}
                missingPositions={missingPositions}
                campaignName="reopen"
                businessId={currentBusiness?._id}
                businessName={currentBusiness?.name}
              />
            )}
          </ScrollView>

          <TouchableOpacity
            style={[s.sendBtn, (!canSend || sending) && s.sendBtnDisabled]}
            onPress={handleSend}
            disabled={!canSend || sending}
            activeOpacity={0.85}>
            {sending
              ? <ActivityIndicator color="#fff" size="small" />
              : <>
                  <Ionicons name="logo-whatsapp" size={18} color="#fff" />
                  <Text style={s.sendBtnText}>Send & Reopen Conversation</Text>
                </>}
          </TouchableOpacity>
        </KeyboardAvoidingView>
      )}
    </>
  );

  if (isWeb) {
    // Web: render as an absolute bottom sheet anchored inside the conversation column
    // No RN Modal — just a positioned overlay within the parent stack
    if (!visible) return null;
    return (
      <View style={s.webOverlay} pointerEvents="box-none">
        <TouchableOpacity style={s.webBackdrop} activeOpacity={1} onPress={handleClose} />
        <View style={s.webSheet}>
          {sheetContent}
        </View>
      </View>
    );
  }

  // Mobile: standard bottom-sheet Modal
  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={handleClose}>
      <View style={s.overlay}>
        <TouchableOpacity style={s.backdrop} activeOpacity={1} onPress={handleClose} />
        <View style={s.sheet}>
          <View style={s.handle} />
          {sheetContent}
        </View>
      </View>
    </Modal>
  );
};

const getS = () => StyleSheet.create({
  // ── Mobile bottom sheet ──────────────────────────────────────────────────────
  overlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.45)' },
  backdrop: { flex: 1 },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    minHeight: '60%',
    maxHeight: '90%',
    paddingHorizontal: spacing.md,
    paddingBottom: Platform.OS === 'ios' ? 34 : spacing.md,
    paddingTop: spacing.sm,
    flex: 1,
  },
  handle: { width: 36, height: 4, borderRadius: 2, backgroundColor: '#ddd', alignSelf: 'center', marginBottom: spacing.sm },

  // ── Web anchored bottom sheet ────────────────────────────────────────────────
  // Positioned absolute so it sits within the conversation column, not full-screen
  webOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    zIndex: 100,
  } as any,
  webBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.35)',
  } as any,
  webSheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    paddingTop: spacing.sm,
    minHeight: '60%',
    maxHeight: '90%',
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 12,
  },

  // ── Shared ───────────────────────────────────────────────────────────────────
  headerBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  headerTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: '#1a1a1a' },
  sub: { fontSize: typography.fontSize.sm, color: '#888', marginBottom: spacing.sm, lineHeight: 20 },

  selectedBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: '#f0f4ff', borderRadius: 8, padding: spacing.sm, marginBottom: spacing.sm,
  },
  selectedBadgeText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  changeText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },

  sendBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm,
    backgroundColor: '#25D366', borderRadius: 12, paddingVertical: 14, marginTop: spacing.sm,
  },
  sendBtnDisabled: { opacity: 0.45 },
  sendBtnText: { color: '#fff', fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.base },
});
